import { useState } from "react";
import toast from "react-hot-toast";
import {
    Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
    Stack, Text, useColorModeValue,
} from "@chakra-ui/react";
import { removeGroupMember } from "../groupsApi";
import { InfoBanner } from "../groupsUi";

/**
 * Maps backend errors to friendly French messages.
 * @param {string} raw - Raw error
 * @returns {string}
 */
function mapRemoveError(raw) {
    if (typeof raw !== "string") return "Échec du retrait";
    if (raw.toLowerCase().includes("leader")) return "Impossible de retirer le leader. Transférez d'abord le leadership.";
    return raw;
}

/**
 * Submits the removal and surfaces toasts.
 * @param {Object} args - Submission args
 */
async function submitRemove({ memberId, setLoading, onClose, onRemoved }) {
    setLoading(true);
    try {
        await removeGroupMember(memberId);
        toast.success("Membre retiré");
        onRemoved?.();
        onClose();
    } catch (error) {
        toast.error(mapRemoveError(error?.response?.data));
    } finally {
        setLoading(false);
    }
}

/**
 * Body of the deletion confirmation.
 * @param {Object} props - { member }
 */
function DialogBody({ member }) {
    const textColor = useColorModeValue("secondaryGray.800", "whiteAlpha.900");
    return (
        <Stack spacing="16px">
            <InfoBanner tone="danger">Le membre recevra un email l'informant de son retrait du groupe.</InfoBanner>
            <Text color={textColor} fontSize="0.85rem">
                Retirer <strong>{member?.surname || member?.email || "ce membre"}</strong> du groupe ?
            </Text>
            {member?.is_leader && (
                <Text color="red.400" fontSize="0.78rem">
                    Attention : ce membre est le leader. Le retrait peut être bloqué côté serveur.
                </Text>
            )}
        </Stack>
    );
}

/**
 * Confirmation dialog for removing a member from a group.
 * @param {Object} props - { member, onClose, onRemoved }
 */
function RemoveMemberDialog({ member, onClose, onRemoved }) {
    const open = Boolean(member);
    const [loading, setLoading] = useState(false);
    const onSubmit = () => submitRemove({ memberId: member?.id, setLoading, onClose, onRemoved });

    return (
        <Modal isOpen={open} onClose={onClose} size="md" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Retirer le membre</ModalHeader>
                <ModalCloseButton />
                <ModalBody><DialogBody member={member} /></ModalBody>
                <ModalFooter gap="8px">
                    <Button variant="ghost" onClick={onClose}>Annuler</Button>
                    <Button colorScheme="red" onClick={onSubmit} isLoading={loading} loadingText="Retrait...">Retirer</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default RemoveMemberDialog;
